import { Badge, Box, HStack, Icon, Td, Text, Tr, useColorModeValue, useTheme } from '@chakra-ui/react';
import { AbsencePeriod, AbsenceRecord, AbsenceType } from '../../types';
import { AbsenceTypeBadge } from './AbsenceTypeBadge';
import { useIntl } from 'react-intl';
import { useSuIntl } from '../../hooks/useSuIntl';
import { TimeRangeVisualizer } from '../common/DateRangeVisualizer';
import {
    MdBeachAccess,
    MdLocalHospital,
    MdLocalPharmacy,
    MdPersonOff,
    MdMoneyOff,
    MdSwapHoriz,
    MdMoreHoriz,
    MdAccessTime,
} from 'react-icons/md';
import { IconType } from 'react-icons';
import { absenceStatusColorMap } from './AbsenceStatusBadge';
import { ChevronRightIcon } from '@chakra-ui/icons';

const absenceTypeIconMap: Record<AbsenceType, IconType> = {
    [AbsenceType.VACATION]: MdBeachAccess,
    [AbsenceType.SICK_LEAVE]: MdLocalHospital,
    [AbsenceType.DOCTOR]: MdLocalPharmacy,
    [AbsenceType.PERSONAL_LEAVE]: MdPersonOff,
    [AbsenceType.UNPAID_LEAVE]: MdMoneyOff,
    [AbsenceType.COMPENSATORY_LEAVE]: MdSwapHoriz,
    [AbsenceType.OTHER]: MdMoreHoriz,
};

type CombinedFeedAbsenceItemProps = {
    item: AbsenceRecord;
    absenceDayIndex?: number;
    absenceTotalDays?: number;
    onClick: () => void;
};

export const CombinedFeedAbsenceItem: React.FC<CombinedFeedAbsenceItemProps> = ({ item, absenceDayIndex, absenceTotalDays, onClick }) => {
    const intl = useIntl();
    const { formatDate } = useSuIntl();
    const theme = useTheme();
    const TypeIcon = absenceTypeIconMap[item.type] || MdMoreHoriz;

    return (
        <Tr
            role="group"
            _hover={{
                bg: useColorModeValue(theme.colors.gray[200], theme.colors.gray[600]),
            }}
            cursor={'pointer'}
            onClick={onClick}
            bg={useColorModeValue('white', 'gray.700')}
        >
            <Td borderLeftRadius={'lg'} borderLeftWidth={4} borderLeftColor={'outline'}>
                <HStack spacing={3}>
                    <Box
                        w={8}
                        h={8}
                        borderRadius={'md'}
                        bg={useColorModeValue('green.100', 'green.900')}
                        display={'flex'}
                        alignItems={'center'}
                        justifyContent={'center'}
                        flexShrink={0}
                    >
                        <Icon as={TypeIcon} boxSize={4} color={'green.500'} />
                    </Box>
                    <Box>
                        <Text fontWeight={600} fontSize="medium">
                            {item.user?.firstName} {item.user?.surname}
                        </Text>
                        <AbsenceTypeBadge type={item.type} size="sm" />
                    </Box>
                </HStack>
            </Td>
            <Td>
                <HStack spacing={2}>
                    <Icon as={MdAccessTime} boxSize={4} color="gray.500" />
                    <TimeRangeVisualizer dateFrom={formatDate(item.dateFrom)} dateTo={formatDate(item.dateTo)} />
                    {item.period === AbsencePeriod.HALF_DAY && (
                        <Badge colorScheme="orange" fontSize="xs">
                            {intl.formatMessage({ id: 'absence.halfDay' })}
                        </Badge>
                    )}
                    {absenceTotalDays !== undefined && absenceTotalDays > 1 && absenceDayIndex !== undefined && (
                        <Text fontSize="sm" color="gray.500">
                            {intl.formatMessage({ id: 'absence.dayOfTotal' }, { day: absenceDayIndex, total: absenceTotalDays })}
                        </Text>
                    )}
                </HStack>
            </Td>
            <Td borderRightRadius={'lg'}>
                <HStack justifyContent={'flex-end'}>
                    <ChevronRightIcon boxSize={5} display={{ base: 'none', md: 'block' }} />
                </HStack>
            </Td>
        </Tr>
    );
};
